import {
  Box,
  Button,
  Card,
  Divider,
  ListItemText,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { toast } from "react-toastify";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { TablePage } from "@outpave/ds";
import { useLazyQuery, useMutation } from "@apollo/client";
import { FREEZE_CARD, GET_CARDS, UNFREZEE_CARD } from "graphql/card";
import CardType from "enums/CardType";
import { DEFAULT_PAGINATION_SCROLL_LIMIT } from "components/common/Constants";
import PlaidLinkWrapper, {
  PlaidAction,
} from "components/common/PlaidLinkWrapper";
import { DISCONNECT_INTEGRATION } from "graphql/integration";
import LoadingWithoutModal from "components/common/LoadingWithoutModal";
import CardStatusType from "enums/CardStatusType";
import AccountStatus from "enums/AccountStatus";
import RenderMerchantDisplay from "components/common/RenderMerchantDisplay";
import ExternalLink from "components/admin/common/ExternalLink";
import useGetAccounts, { ExternalAccount } from "hooks/useGetAccounts";
import { useNavBreadcrumbs } from "hooks/useNavBreadcrumbs";
import { fullName } from "utils/fullName";

interface Institution {
  accountId: string;
  name: string;
  website: string;
  status: string;
  cards: Card[];
  loadStatus: "NEW" | "NEXTPAGE" | "COMPLETE";
  endCursor?: string;
}

interface Card {
  id: string;
  type: string;
  name: string;
  user: string;
  status: string;
  number: string;
}

/**
 * The Plaid Linked Accounts screen, moved out of pages/admin/LinkedAccounts.tsx. See README.md.
 */
const PlaidLinkedAccounts: React.FC = () => {
  const { t } = useTranslation();
  const breadcrumbs = useNavBreadcrumbs(t("Linked Accounts"));

  const [loading, setLoading] = useState(true);
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [isNextPage, setIsNextPage] = useState(false);
  const [openLink, setOpenLink] = useState(false);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [plaidAction, setPlaidAction] = useState<PlaidAction>();

  const [getCards, { loading: loadingCards }] = useLazyQuery(GET_CARDS, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const { refetch: refetchAccounts, loading: loadingAccounts } = useGetAccounts(
    {
      onCompleted: (data) => {
        const institutions: Institution[] = data.accounts.map(
          (account: ExternalAccount) => ({
            accountId: account.id,
            name: account.name,
            website: account.url,
            status: account.status,
            cards: [],
            loadStatus: "NEW",
          }),
        );
        loadInstitutions(institutions, true);
      },
    },
  );

  const [disconnectIntegration, { loading: disconnecting }] = useMutation(
    DISCONNECT_INTEGRATION,
    {
      onCompleted: () => {
        refetchAccounts();
      },
      onError: (error) => {
        toast.error(`Failed to disconnect integration: ${error.message}`);
      },
    },
  );

  const loadInstitutions = async (
    inputInstitutions: Institution[],
    reset: boolean,
  ) => {
    let updatedInstitutions = reset ? [] : institutions;

    for (const institution of inputInstitutions) {
      // Skip if it's already complete
      if (institution.loadStatus === "COMPLETE") continue;

      const { data } = await getCards({
        variables: {
          accountId: institution.accountId,
          cardType: CardType.EXTERNAL,
          status: [CardStatusType.ACTIVE],
          ...(institution.endCursor && { after: institution.endCursor }),
        },
      });

      const { edges = [], pageInfo } = data?.cards || {};
      const newCards = edges.map((edge: any) => ({
        id: edge.node.id,
        type: edge.node.type,
        name: edge.node.nickname,
        status: edge.node.status,
        user: fullName(edge.node.user),
        number: edge.node.cardNumber,
      }));

      // Spread the new cards into the existing array
      institution.cards.push(...newCards);

      // Update load status and cursor
      if (pageInfo?.hasNextPage) {
        institution.loadStatus = "NEXTPAGE";
        institution.endCursor = pageInfo.endCursor;
        setIsNextPage(true);
      } else {
        institution.loadStatus = "COMPLETE";
      }
      updatedInstitutions.push(institution);
    }

    // Trigger a state update so React re-renders
    setInstitutions(updatedInstitutions);
  };

  const handleOpenAddAccount = () => {
    setAccountId(null);
    setPlaidAction(PlaidAction.ADD_ACCOUNT);
    setOpenLink(true);
  };

  const handleOpenEditAccount = (accountId: string) => {
    setAccountId(accountId);
    setPlaidAction(PlaidAction.EDIT_ACCOUNT);
    setOpenLink(true);
  };

  const handleModalClosed = () => {
    setAccountId(null);
    setOpenLink(false);
    setPlaidAction(undefined);
    refetchAccounts();
  };

  const handleReconnectAccount = (accountId: string) => {
    setAccountId(accountId);
    setPlaidAction(PlaidAction.RECONNECT);
    setOpenLink(true);
  };

  const handleDisconnectAccount = (accountId: string) => {
    setPlaidAction(undefined);
    setAccountId(null);
    setOpenLink(false);
    disconnectIntegration({
      variables: {
        accountId,
      },
    });
  };

  const scrollRef = useRef(null);
  const handleScroll = useCallback(
    (e: Event) => {
      const target = e.target as HTMLDivElement;
      const { scrollTop, scrollHeight, clientHeight } = target;
      const isNearBottom =
        (scrollTop + clientHeight) / scrollHeight >=
        DEFAULT_PAGINATION_SCROLL_LIMIT;

      if (isNearBottom && isNextPage) {
        loadInstitutions(institutions, false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [institutions, isNextPage],
  );

  useEffect(() => {
    const ref = scrollRef.current as HTMLElement | null;
    ref?.addEventListener("scroll", handleScroll);
    return () => ref?.removeEventListener("scroll", handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoading(loadingAccounts || loadingCards || disconnecting);
  }, [loadingAccounts, loadingCards, disconnecting]);

  return (
    <TablePage
      title={t("Linked Accounts")}
      breadcrumbs={breadcrumbs}
      primaryAction={
        <Button onClick={() => handleOpenAddAccount()}>
          {t("Add Account")}
        </Button>
      }
    >
      <Box
        sx={{
          backgroundColor: "var(--color-bg-page)",
          height: "100%",
          paddingInline: "var(--space-7)",
          paddingBlock: "var(--space-7)",
          flexGrow: 1,
          overflow: "auto",
        }}
        ref={scrollRef}
      >
        <Stack gap="var(--space-9)">
          {institutions?.length > 0
            ? institutions.map((institution) => (
                <InstitutionCard
                  key={institution.name}
                  institution={institution}
                  editAccount={handleOpenEditAccount}
                  reconnectAccount={handleReconnectAccount}
                  disconnectAccount={handleDisconnectAccount}
                  loading={loading}
                  t={t}
                />
              ))
            : !loading && (
                <Card
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "384px",
                    textAlign: "center",
                    padding: "var(--space-7)",
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{ marginBottom: "var(--space-5)" }}
                  >
                    {t("No linked accounts")}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ marginBottom: "var(--space-7)" }}
                  >
                    {t("AddFirstLinkedAccountMessage")}
                  </Typography>
                  <Button
                    variant="primary-outlined"
                    size="large"
                    onClick={handleOpenAddAccount}
                    disableElevation
                    sx={{ height: "48px" }}
                  >
                    {t("Add Account")}
                  </Button>
                </Card>
              )}
        </Stack>
      </Box>
      <LoadingWithoutModal open={loading} />
      <PlaidLinkWrapper
        visible={openLink}
        onModalClosed={handleModalClosed}
        onSuccess={() => {
          refetchAccounts();
          toast.success(t("Account connected successfully"));
        }}
        existingAccountId={accountId}
        action={plaidAction}
      />
    </TablePage>
  );
};

interface InstitutionCardProps {
  institution: Institution;
  editAccount: (accountId: string) => void;
  reconnectAccount: (accountId: string) => void;
  disconnectAccount: (accountId: string) => void;
  loading: boolean;
  t: any;
}

const InstitutionCard: React.FC<InstitutionCardProps> = ({
  institution,
  editAccount,
  reconnectAccount,
  disconnectAccount,
  loading,
  t,
}) => {
  return (
    <Card variant="outlined" sx={{ padding: "var(--space-7)", boxShadow: 1 }}>
      <Stack
        gap="0"
        sx={{
          justifyContent: "space-between",
          marginBottom: "var(--space-7)",
          width: "100%",
        }}
      >
        <Inline gap="var(--space-5)" wrap={false} sx={{ width: "100%" }}>
          <ExternalLink to={institution.website}>
            <RenderMerchantDisplay
              merchant={institution.name}
              url={institution.website}
              logoType="logo"
              fallback="lettermark"
              width={100}
              theme="dark"
              withTitle={false}
            />
          </ExternalLink>
          <Typography variant="h4" noWrap>
            {institution.name}
          </Typography>
          <Inline
            wrap={false}
            justify="end"
            gap="var(--space-3)"
            sx={{ width: "100%" }}
          >
            {institution.status === AccountStatus.DISCONNECTED && (
              <>
                <Button
                  variant="primary-filled"
                  size="medium"
                  onClick={() => reconnectAccount(institution.accountId)}
                  fullWidth
                >
                  {t("Reconnect")}
                </Button>
                <Button
                  variant="primary-outlined"
                  size="medium"
                  onClick={() => disconnectAccount(institution.accountId)}
                  fullWidth
                >
                  {t("Disconnect")}
                </Button>
              </>
            )}
            {institution.status === AccountStatus.ACTIVE && (
              <>
                <Button
                  variant="primary-filled"
                  size="medium"
                  onClick={() => editAccount(institution.accountId)}
                  fullWidth
                >
                  {t("Edit")}
                </Button>
                <Button
                  variant="primary-outlined"
                  size="medium"
                  onClick={() => disconnectAccount(institution.accountId)}
                  fullWidth
                >
                  {t("Disconnect")}
                </Button>
              </>
            )}
          </Inline>
        </Inline>
        <Box>
          <Typography variant="body1">{t(institution.status)}</Typography>
        </Box>
      </Stack>
      <Stack gap="var(--space-5)">
        {institution.cards.map((card, idx) => (
          <Box key={idx}>
            <AccountRow card={card} />
            {idx < institution.cards.length - 1 && (
              <Divider sx={{ marginTop: "var(--space-5)" }} />
            )}
          </Box>
        ))}
      </Stack>
      <LoadingWithoutModal open={loading} />
    </Card>
  );
};

interface AccountRowProps {
  card: Card;
}

const AccountRow: React.FC<AccountRowProps> = ({ card }) => {
  const [loading, setLoading] = useState(false);
  const [cardStatus, setCardStatus] = useState(
    card.status === CardStatusType.ACTIVE,
  );
  const [freezeCard, { loading: freezingCard }] = useMutation(FREEZE_CARD, {
    onCompleted: (data) => {
      setCardStatus(false);
    },
  });

  const [unFreezeCard, { loading: unFreezingCard }] = useMutation(
    UNFREZEE_CARD,
    {
      onCompleted: (data) => {
        setCardStatus(true);
      },
    },
  );

  const handleFreezeCard = (cardId: string, enabled: boolean) => {
    if (enabled) {
      unFreezeCard({
        variables: {
          id: cardId,
        },
      });
    } else {
      freezeCard({
        variables: {
          id: cardId,
        },
      });
    }
  };

  useEffect(() => {
    setLoading(freezingCard || unFreezingCard);
  }, [freezingCard, unFreezingCard]);

  return (
    <Inline wrap={false} justify="between" gap="0">
      <Inline wrap={false} align="start" gap="var(--space-4)">
        <ToggleSwitch
          enabled={cardStatus}
          onChange={(enabled: boolean) => handleFreezeCard(card.id, enabled)}
        />
        <Box>
          <ListItemText
            sx={{ textWrap: "wrap" }}
            primary={card.name}
            secondary={card.type + " | " + card.number + " | " + card.user}
          />
        </Box>
      </Inline>
      <LoadingWithoutModal open={loading} />
    </Inline>
  );
};

interface ToggleSwitchProps {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({ enabled, onChange }) => {
  return (
    <Tooltip title="Toggle import of data">
      <Switch
        checked={enabled}
        onChange={(event) => {
          onChange(event.target.checked);
        }}
        sx={{ marginRight: "var(--space-5)" }}
        slotProps={{
          input: { "aria-label": "controlled" },
        }}
      />
    </Tooltip>
  );
};

export default PlaidLinkedAccounts;
